import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useSafeProductSubscriptions } from "@/modules/productSubscriptions";
import { ReactComponent as AgelessAiLogo } from "assets/ageless-logo.svg";
import { ReactComponent as RepeatMdLogo } from "assets/repeatmd-logo.svg";
import { Button } from "atoms/Button";
import { ReactComponent as FrownIcon } from "atoms/icons/frown.svg";

export type FatalErrorViewProps = {
  "className"?: string;
  "data-testid"?: string;
};

export const FatalErrorView = ({
  className = "flex min-h-screen w-full items-center justify-center bg-surface-muted",
  "data-testid": dataTestId,
}: FatalErrorViewProps) => {
  const navigate = useNavigate();
  const subscriptionsContext = useSafeProductSubscriptions();

  const { logo, supportEmail } = useMemo(() => {
    const caps = subscriptionsContext?.subscriptions ?? [];
    const hasRepeatMD = caps.includes("RepeatMD");
    const hasAgeless = caps.includes("AgelessAi");

    if (hasRepeatMD && hasAgeless) {
      return {
        logo: (
          <div className="flex items-center gap-4">
            <AgelessAiLogo className="h-8 w-12" />
            <span className="text-neutral-500">×</span>
            <RepeatMdLogo className="h-7 w-16" />
          </div>
        ),
        supportEmail: "support@repeatmd.com",
      };
    }

    if (hasAgeless) {
      return {
        logo: <AgelessAiLogo className="h-8 w-12" />,
        supportEmail: "support@ageless.ai",
      };
    }

    return {
      logo: <RepeatMdLogo className="h-7 w-16" />,
      supportEmail: "support@repeatmd.com",
    };
  }, [subscriptionsContext?.subscriptions]);

  return (
    <div className={className}>
      <div
        className="flex flex-col items-center justify-center px-2 text-center"
        data-testid={dataTestId}
      >
        <div className="mb-6">{logo}</div>
        <FrownIcon title="Sad face" />
        <p className="mt-4 text-body1 font-bold">
          Sorry, we ran into an issue – please try refreshing this page.
        </p>
        <p className="mt-4 text-body2">
          If this recurs, please contact us at{" "}
          <a className="text-primary" href={`mailto:${supportEmail}`}>
            {supportEmail}
          </a>
        </p>
        <div className="mt-8 flex gap-8">
          <Button style="outline" onClick={() => navigate("/admin")}>
            Go to your home screen
          </Button>
          <Button style="outline" onClick={() => globalThis.location.reload()}>
            Refresh this page
          </Button>
        </div>
      </div>
    </div>
  );
};
